import React, { useRef, useEffect, useState } from "react";
import { PrimaryButton, SecondaryButton } from "@/components/Buttons.jsx";
import EmailEditor from "react-email-editor";
import { uploadImageToS3 } from "@/api/upload.js";
import { getCrmCampaignTemplates, generateTemplateWithAI } from "@/api/crm.js";
import TemplateSelectionModal from "./TemplateSelectionModal.jsx";
import AIGenerationModal from "./AIGenerationModal.jsx";
import ModalBasic from "./ModalBasic.jsx";

const EmailEditorModal = ({
  isOpen,
  onClose,
  onSave,
  initialDesign = null,
  title = "Edit Email Content",
  minHeight = 800,
  modalSize = "max-w-[1200px]",
  modalMinHeight = 900,
  additionalMergeTags = {},
  templateType = null,
}) => {
  const emailEditorRef = useRef();
  const [templates, setTemplates] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);

  // Fetch campaign templates when templateType is provided
  useEffect(() => {
    if (isOpen && templateType) {
      const fetchTemplates = async () => {
        try {
          const response = await getCrmCampaignTemplates(templateType);

          if (response.status === "success" && response.data) {
            setTemplates(response.data);
          }
        } catch (error) {
        }
      };

      fetchTemplates();
    } else {
      // Clear templates when not using templateType
      setTemplates([]);
    }
  }, [isOpen, templateType]);

  // Handle template selection from modal
  const handleTemplateSelect = (design) => {
    if (emailEditorRef.current && emailEditorRef.current.editor) {
      if (design) {
        emailEditorRef.current.editor.loadDesign(design);
      } else {
        // Blank template - clear the editor
        emailEditorRef.current.editor.loadDesign({
          body: {
            id: "body-blank",
            rows: [],
            values: {
              backgroundColor: "#ffffff",
              contentWidth: "500px",
              fontFamily: {
                label: "Arial",
                value: "arial,helvetica,sans-serif"
              },
              textColor: "#000000"
            }
          },
          counters: {},
          schemaVersion: 17
        });
      }
    }
    setShowTemplateModal(false);
  };

  // Handle AI generation
  const handleAIGenerate = async (prompt) => {
    try {
      // Get current design from email editor
      const currentDesign = await new Promise((resolve) => {
        if (emailEditorRef.current && emailEditorRef.current.editor) {
          emailEditorRef.current.editor.saveDesign((design) => {
            resolve(design);
          });
        } else {
          resolve(null);
        }
      });

      let includeCurrentDesign = false;
      if(currentDesign?.counters?.u_column > 2 && currentDesign?.counters?.u_row > 2) {
        includeCurrentDesign = true;
      }

      const response = await generateTemplateWithAI({
        prompt,
        design: includeCurrentDesign ? currentDesign : null,
        campaign_type: templateType || "all"
      });
      
      // Load the generated design into the email editor
      if (response.status === 'success' && response.generated_design) {
        if (emailEditorRef.current && emailEditorRef.current.editor) {
          emailEditorRef.current.editor.loadDesign(response.generated_design);
        }
      }
      
      // Close the AI generation modal
      setShowAIModal(false);
      
    } catch (error) {
      // TODO: Show error message to user
    }
  };

  const handleSave = () => {
    if (emailEditorRef.current && emailEditorRef.current.editor) {
      emailEditorRef.current.editor.exportHtml((data) => {
        onSave(data, true);
        onClose();
      });
    }
  };

  const handleLoad = () => {
    if (emailEditorRef.current && emailEditorRef.current.editor) {
      if (initialDesign) {
        emailEditorRef.current.editor.loadDesign(initialDesign);
      }
      emailEditorRef.current.editor.registerCallback(
        "image",
        async function (file, done) {
          const imageUrl = await uploadImageToS3(file.attachments[0]);
          done({ progress: 100, url: imageUrl });
        }
      );
    }
  };

  return (
    <>
      <ModalBasic
        title={title}
        modalOpen={isOpen}
        setModalOpen={onClose}
        width="1200"
        hideCloseButton={false}
      >
        <div className="flex flex-col gap-4" style={{ minHeight: modalMinHeight }}>
          <div className="flex items-center justify-start gap-2 mb-2">
            {/* Template Selection Button */}
            {templates.length > 0 && (
              <PrimaryButton size="sm" onClick={() => setShowTemplateModal(true)}>
                Or start with a template
              </PrimaryButton>
            )}

            {/* AI Generation Button */}
            <PrimaryButton size="sm" onClick={() => setShowAIModal(true)}>
              ✨ SmartGenerate
            </PrimaryButton>
          </div>

          <div className="flex-1 min-h-[800px] flex gap-6">
            <div className="w-full h-[800px]">
              <EmailEditor
                ref={emailEditorRef}
                minHeight={minHeight}
                onLoad={handleLoad}
                options={{
                  mergeTags: {
                    first_name: {
                      name: "First Name",
                      value: "{contactfield=firstname}",
                      sample: "John",
                    },
                    last_name: {
                      name: "Last Name",
                      value: "{contactfield=lastname}",
                      sample: "Doe",
                    },
                    last_added_item_name: {
                      name: "Last Added Item Name",
                      value: "{contactfield=last_added_item_name}",
                      sample: "Some Product Name",
                    },
                    last_added_item_image: {
                      name: "Last Added Item Image",
                      value: "{contactfield=last_added_item_image}",
                      sample: "https://cdn.saucey.com/direct/web/crm-placehold-product.png",
                    },
                    last_shopping_store_url: {
                      name: "URL of last shopped store",
                      value: "{contactfield=last_shopping_store_url}",
                      sample: "https://yourstore.com",
                    },
                    ...additionalMergeTags,
                  },
                  tools: {
                    text: {
                      properties: {
                        textAlign: {
                          value: "center",
                        },
                      },
                    },
                  },
                  features: {
                    devTab: false,
                  },
                  version: "1.157.0",
                }}
              />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
            <PrimaryButton onClick={handleSave}>Save</PrimaryButton>
          </div>
        </div>
      </ModalBasic>

      {/* Template Selection Modal */}
      <TemplateSelectionModal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        templates={templates}
        onTemplateSelect={handleTemplateSelect}
      />

      {/* AI Generation Modal */}
      <AIGenerationModal
        isOpen={showAIModal}
        onClose={() => setShowAIModal(false)}
        onGenerate={handleAIGenerate}
      />
    </>
  );
};

export default EmailEditorModal;
